import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * MediaContext — persists Media page UI state across navigation AND F5 refresh.
 * Lives above the router so it never unmounts when switching pages.
 *
 * Architecture: ONE context per top-level page (same as AnalyticsContext).
 * Covers all three Media sub-tabs: Event Videos, Live Videos, Remote Downloads.
 *
 * IMPORTANT: Each Media sub-tab (`<EventVideos>`, `<LiveVideos>`, `<RemoteDownloads>`)
 * is conditionally rendered by `Media.jsx`. They unmount when their tab is not active,
 * which would destroy any local useState. This is the same pattern that bit the
 * EventRulesManager search bar (see /app/memory/REFACTOR_NOTES.md). Therefore the
 * persisted state for each sub-tab MUST live in this context, not in the child.
 *
 * Sub-tab children use Direct Consume (destructure from useMedia()) with local
 * variable aliases so existing consumers don't need refactoring.
 *
 * Dates (single Date instances) round-trip as ISO strings via serializeDate /
 * hydrateDate; Sets round-trip as arrays.
 */
const MediaContext = createContext(null);

export const useMedia = () => {
  const ctx = useContext(MediaContext);
  if (!ctx) throw new Error('useMedia must be used within MediaProvider');
  return ctx;
};

const STORAGE_KEY = 'mp_state';

const loadPersistedState = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
};

// Helper: serialize a single Date | null to ISO string | null
export const serializeDate = (d) => {
  if (!d) return null;
  if (d instanceof Date) return isNaN(d.getTime()) ? null : d.toISOString();
  if (typeof d === 'string') return d;
  return null;
};

// Helper: hydrate ISO string | null back to a Date object, with a fallback
export const hydrateDate = (iso, fallback) => {
  if (!iso) return fallback;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? fallback : d;
};

export const MediaProvider = ({ children }) => {
  const saved = loadPersistedState();

  // ── Parent: active sub-tab (URL `?tab=` still wins on first load) ──
  const [persistedActiveTab, setPersistedActiveTab] = useState(saved?.activeTab || null);

  // ── EventVideos (Event Videos sub-tab) ──
  const [evSelectedDeviceIds, setEvSelectedDeviceIds] = useState(saved?.evSelectedDeviceIds || []);
  const [evSelectedEventType, setEvSelectedEventType] = useState(saved?.evSelectedEventType || 'all');
  const [evSelectedChannel, setEvSelectedChannel] = useState(saved?.evSelectedChannel || 'all');
  const [evSelectedDateIso, setEvSelectedDateIso] = useState(saved?.evSelectedDateIso || null);
  const [evTimeFrom, setEvTimeFrom] = useState(saved?.evTimeFrom || '');
  const [evTimeTo, setEvTimeTo] = useState(saved?.evTimeTo || '');
  const [evMonthDateIso, setEvMonthDateIso] = useState(saved?.evMonthDateIso || null);

  // ── LiveVideos (Live Streams sub-tab) ──
  const [lvSelectedDeviceId, setLvSelectedDeviceId] = useState(saved?.lvSelectedDeviceId || null);
  const [lvDeviceStatusFilter, setLvDeviceStatusFilter] = useState(saved?.lvDeviceStatusFilter || 'all');

  // ── RemoteDownloads (SD Card recordings sub-tab) ──
  const [rdSelectedDevice, setRdSelectedDevice] = useState(saved?.rdSelectedDevice || '');
  const [rdMonthDateIso, setRdMonthDateIso] = useState(saved?.rdMonthDateIso || null);
  const [rdSelectedDateIso, setRdSelectedDateIso] = useState(saved?.rdSelectedDateIso || null);
  const [rdStartTime, setRdStartTime] = useState(saved?.rdStartTime || '00:00:00');
  const [rdEndTime, setRdEndTime] = useState(saved?.rdEndTime || '23:59:59');
  const [rdStorageType, setRdStorageType] = useState(saved?.rdStorageType || '0');
  const [rdStreamType, setRdStreamType] = useState(saved?.rdStreamType || 'all');
  const [rdVideoType, setRdVideoType] = useState(saved?.rdVideoType || 'all');
  const [rdSelectedChannels, setRdSelectedChannels] = useState(saved?.rdSelectedChannels || []);

  // ── SharedEvidence (cross-source share-link audit sub-tab) ──
  const [seSearchQuery, setSeSearchQuery] = useState(saved?.seSearchQuery || '');
  const [seSourceFilter, setSeSourceFilter] = useState(saved?.seSourceFilter || 'all'); // all | event_video | remote_download
  const [seStatusFilter, setSeStatusFilter] = useState(saved?.seStatusFilter || 'all'); // all | active | revoked | expired
  const [seSortField, setSeSortField] = useState(saved?.seSortField || 'created_at');
  const [seSortDir, setSeSortDir] = useState(saved?.seSortDir || 'desc'); // asc | desc
  // Date-range filter (matches Command Center → Command History / Audit
  // Log pattern). Stored as ISO strings so sessionStorage round-trips them.
  const [seDateFromIso, setSeDateFromIso] = useState(saved?.seDateFromIso || null);
  const [seDateToIso, setSeDateToIso] = useState(saved?.seDateToIso || null);

  useEffect(() => {
    const state = {
      activeTab: persistedActiveTab,
      // EventVideos
      evSelectedDeviceIds, evSelectedEventType, evSelectedChannel,
      evSelectedDateIso, evTimeFrom, evTimeTo, evMonthDateIso,
      // LiveVideos
      lvSelectedDeviceId, lvDeviceStatusFilter,
      // RemoteDownloads
      rdSelectedDevice, rdMonthDateIso, rdSelectedDateIso,
      rdStartTime, rdEndTime,
      rdStorageType, rdStreamType, rdVideoType,
      rdSelectedChannels,
      // SharedEvidence
      seSearchQuery, seSourceFilter, seStatusFilter,
      seSortField, seSortDir,
      seDateFromIso, seDateToIso,
    };
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* ignore */ }
  }, [persistedActiveTab,
      evSelectedDeviceIds, evSelectedEventType, evSelectedChannel,
      evSelectedDateIso, evTimeFrom, evTimeTo, evMonthDateIso,
      lvSelectedDeviceId, lvDeviceStatusFilter,
      rdSelectedDevice, rdMonthDateIso, rdSelectedDateIso,
      rdStartTime, rdEndTime,
      rdStorageType, rdStreamType, rdVideoType,
      rdSelectedChannels,
      seSearchQuery, seSourceFilter, seStatusFilter,
      seSortField, seSortDir,
      seDateFromIso, seDateToIso]);

  return (
    <MediaContext.Provider value={{
      persistedActiveTab, setPersistedActiveTab,

      evSelectedDeviceIds, setEvSelectedDeviceIds,
      evSelectedEventType, setEvSelectedEventType,
      evSelectedChannel, setEvSelectedChannel,
      evSelectedDateIso, setEvSelectedDateIso,
      evTimeFrom, setEvTimeFrom,
      evTimeTo, setEvTimeTo,
      evMonthDateIso, setEvMonthDateIso,

      lvSelectedDeviceId, setLvSelectedDeviceId,
      lvDeviceStatusFilter, setLvDeviceStatusFilter,

      rdSelectedDevice, setRdSelectedDevice,
      rdMonthDateIso, setRdMonthDateIso,
      rdSelectedDateIso, setRdSelectedDateIso,
      rdStartTime, setRdStartTime,
      rdEndTime, setRdEndTime,
      rdStorageType, setRdStorageType,
      rdStreamType, setRdStreamType,
      rdVideoType, setRdVideoType,
      rdSelectedChannels, setRdSelectedChannels,

      seSearchQuery, setSeSearchQuery,
      seSourceFilter, setSeSourceFilter,
      seStatusFilter, setSeStatusFilter,
      seSortField, setSeSortField,
      seSortDir, setSeSortDir,
      seDateFromIso, setSeDateFromIso,
      seDateToIso, setSeDateToIso,
    }}>
      {children}
    </MediaContext.Provider>
  );
};
